import { useState, useEffect, useMemo, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import { petSitterService } from '../services/petSitterService'
import { User, Mail, Phone, Camera, Save, X, AlertCircle, CheckCircle, Loader } from 'lucide-react'
import Card, { CardHeader, CardContent, CardFooter } from '../components/ui/Card'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Avatar from '../components/ui/Avatar'
import api from '../services/api'
import { getAvatarUrl } from '../utils/avatarHelpers'

export default function Perfil() {
  const { user, setUser, refreshUser } = useAuth()
  const { isPetSitter } = usePermissions()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [initializing, setInitializing] = useState(true)
  const fileInputRef = useRef(null)

  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    avatar: null,
    estilo_perfil: ''
  })

  const [avatarPreview, setAvatarPreview] = useState(null)
  const [formErrors, setFormErrors] = useState({})

  const previewUser = useMemo(() => {
    if (!user) return null
    if (!avatarPreview) return user
    return {
      ...user,
      avatar: avatarPreview,
      foto: avatarPreview,
      avatarUpdatedAt: avatarPreview
    }
  }, [user, avatarPreview])

  useEffect(() => {
    try {
      if (user) {
        setFormData({
          nombre: user.nombre || '',
          apellido: user.apellido || '',
          email: user.email || '',
          telefono: user.telefono || '',
          avatar: null,
          estilo_perfil: user.petSitters?.[0]?.estilo_perfil || user.pet_sitters?.[0]?.estilo_perfil || ''
        })
        
        // Cargar avatar existente si existe
        const avatarUrl = getAvatarUrl(user)
        if (avatarUrl) {
          setAvatarPreview(avatarUrl)
        }
      }
      setInitializing(false)
    } catch (err) {
      console.error('Error inicializando perfil:', err)
      setError('Error al cargar datos del perfil')
      setInitializing(false)
    }
  }, [user])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Limpiar error del campo cuando el usuario empieza a escribir
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona un archivo de imagen válido')
      return
    }

    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('La imagen no debe superar los 5MB')
      return
    }

    setFormData(prev => ({
      ...prev,
      avatar: file
    }))

    // Crear preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setAvatarPreview(reader.result)
    }
    reader.readAsDataURL(file)
    setError(null)
  }

  const handleRemoveAvatar = () => {
    setFormData(prev => ({
      ...prev,
      avatar: null
    }))
    setAvatarPreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(null)
    setFormErrors({})

    try {
      const userId = user.id_usuario || user.idUsuario
      
      // Crear FormData para enviar datos incluyendo el avatar
      const formDataToSend = new FormData()
      formDataToSend.append('nombre', formData.nombre)
      formDataToSend.append('apellido', formData.apellido)
      formDataToSend.append('telefono', formData.telefono)
      
      // Solo agregar avatar si se seleccionó uno nuevo y es un archivo válido
      if (formData.avatar && formData.avatar instanceof File && formData.avatar.size > 0) {
        // Validar tamaño antes de enviar (máximo 5MB)
        const maxSize = 5 * 1024 * 1024 // 5MB
        if (formData.avatar.size > maxSize) {
          setError(`El archivo es demasiado grande (${(formData.avatar.size / 1024 / 1024).toFixed(2)}MB). El tamaño máximo permitido es 5MB.`)
          setLoading(false)
          return
        }
        
        console.log('📤 Enviando avatar:', {
          name: formData.avatar.name,
          size: formData.avatar.size,
          sizeMB: (formData.avatar.size / 1024 / 1024).toFixed(2),
          type: formData.avatar.type,
          lastModified: formData.avatar.lastModified
        })
        
        // Asegurarse de que el archivo tenga un nombre válido con extensión
        let fileName = formData.avatar.name || 'avatar.jpg'
        // Limpiar el nombre del archivo de caracteres especiales
        fileName = fileName.replace(/[^a-zA-Z0-9._-]/g, '_')
        // Si no tiene extensión, agregar una basada en el tipo MIME
        if (!fileName.includes('.')) {
          const extension = formData.avatar.type.includes('png') ? 'png' :
                           formData.avatar.type.includes('gif') ? 'gif' :
                           formData.avatar.type.includes('webp') ? 'webp' : 'jpg'
          fileName = `avatar.${extension}`
        }
        
        // Agregar el archivo al FormData con el nombre correcto
        // El tercer parámetro es el nombre del archivo que se enviará
        formDataToSend.append('avatar', formData.avatar, fileName)
        
        // Verificar que el archivo se agregó correctamente
        console.log('✅ Avatar agregado al FormData:', fileName)
      } else {
        console.log('⚠️ No hay avatar para enviar o no es un archivo válido')
      }

      // Debug: Verificar contenido del FormData
      console.log('📤 Enviando datos del perfil:', {
        nombre: formData.nombre,
        apellido: formData.apellido,
        telefono: formData.telefono,
        tieneAvatar: !!formData.avatar,
        esArchivo: formData.avatar instanceof File
      })

      // Verificar que el FormData tiene el avatar si se supone que lo tiene
      const formDataEntries = Array.from(formDataToSend.entries())
      const hasAvatar = formDataEntries.some(([key, value]) => key === 'avatar')
      
      console.log('🔍 Verificación completa del FormData antes de enviar:', {
        totalEntries: formDataEntries.length,
        tieneAvatar: hasAvatar,
        entries: formDataEntries.map(([key, value]) => {
          if (value instanceof File) {
            return {
              key,
              type: 'File',
              name: value.name,
              size: value.size,
              mimeType: value.type,
              lastModified: value.lastModified
            }
          }
          return {
            key,
            type: typeof value,
            value: value
          }
        })
      })

      // Log del endpoint y método
      const endpoint = '/auth/profile'
      console.log('🌐 Enviando petición a:', {
        endpoint,
        method: 'POST',
        baseURL: import.meta.env.VITE_API_URL || '/api',
        fullURL: `${import.meta.env.VITE_API_URL || 'http://localhost:8000/api'}${endpoint}`
      })

      // Actualizar perfil
      // Usar POST en lugar de PUT porque Laravel tiene problemas con archivos en PUT
      // NO establecer Content-Type manualmente, axios lo hace automáticamente para FormData
      // El interceptor en api.js ya maneja esto correctamente
      const response = await api.post(endpoint, formDataToSend)
      
      console.log('✅ Respuesta del servidor:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        headers: response.headers
      })
      
      // Verificar si la respuesta incluye información del avatar
      if (response.data?.data) {
        console.log('📋 Datos del usuario en la respuesta:', {
          id: response.data.data.id_usuario || response.data.data.idUsuario,
          nombre: response.data.data.nombre,
          avatar: response.data.data.avatar,
          foto: response.data.data.foto,
          avatarType: typeof response.data.data.avatar,
          fotoType: typeof response.data.data.foto
        })
      }

      if (response.data.success) {
        if (isPetSitter) {
          try {
            await petSitterService.updateMe({
              estilo_perfil: formData.estilo_perfil === '' ? null : formData.estilo_perfil
            })
          } catch (psErr) {
            setError(psErr.response?.data?.error || 'No se pudo guardar el estilo de cuidador (Pro / De onda).')
            setLoading(false)
            return
          }
        }

        const updatedUser = response.data.data
        
        console.log('✅ Usuario actualizado recibido:', {
          id: updatedUser.id_usuario,
          nombre: updatedUser.nombre,
          avatar: updatedUser.avatar,
          foto: updatedUser.foto
        })
        
        // Normalizar la URL del avatar usando la utilidad
        console.log('🔍 Avatar antes de normalizar:', {
          avatar: updatedUser.avatar,
          foto: updatedUser.foto,
          tipoAvatar: typeof updatedUser.avatar,
          tipoFoto: typeof updatedUser.foto
        })
        
        const normalizedAvatarUrl = getAvatarUrl(updatedUser)
        console.log('🔍 URL normalizada del avatar:', normalizedAvatarUrl)
        
        if (normalizedAvatarUrl) {
          updatedUser.avatar = normalizedAvatarUrl
          updatedUser.foto = normalizedAvatarUrl
        } else {
          // Si no se pudo normalizar, intentar construir la URL manualmente
          const avatarUrl = updatedUser.avatar || updatedUser.foto
          if (avatarUrl && typeof avatarUrl === 'string') {
            const baseUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000'
            if (avatarUrl.startsWith('/storage/')) {
              updatedUser.avatar = `${baseUrl}${avatarUrl}`
              updatedUser.foto = `${baseUrl}${avatarUrl}`
            } else if (!avatarUrl.startsWith('http')) {
              updatedUser.avatar = `${baseUrl}/storage/${avatarUrl}`
              updatedUser.foto = `${baseUrl}/storage/${avatarUrl}`
            }
          }
        }
        
        // Agregar timestamp para forzar re-render de componentes que usan el avatar
        updatedUser.avatarUpdatedAt = Date.now()
        
        console.log('🔄 Actualizando contexto con usuario:', {
          avatar: updatedUser.avatar,
          foto: updatedUser.foto,
          avatarUpdatedAt: updatedUser.avatarUpdatedAt
        })
        
        // Actualizar el usuario en el contexto (esto actualizará automáticamente el header)
        setUser({ ...updatedUser })
        
        // Actualizar localStorage
        localStorage.setItem('user', JSON.stringify(updatedUser))
        
        // Refrescar el usuario desde el backend para obtener la información más actualizada
        // Esto asegura que tenemos la foto/avatar correcta que el backend devuelve
        try {
          if (refreshUser) {
            console.log('🔄 Refrescando usuario desde /me para obtener datos actualizados...')
            const refreshedUser = await refreshUser()
            if (refreshedUser) {
              console.log('✅ Usuario refrescado desde /me:', {
                avatar: refreshedUser.avatar,
                foto: refreshedUser.foto
              })
            }
          }
        } catch (refreshError) {
          console.warn('⚠️ Error al refrescar usuario, usando datos de la respuesta:', refreshError)
        }
        
        console.log('✅ Perfil actualizado exitosamente')
        setSuccess('Perfil actualizado exitosamente')
        
        // Limpiar el formulario de avatar
        setFormData(prev => ({
          ...prev,
          avatar: null
        }))
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        
        // Actualizar preview con el nuevo avatar
        const newAvatarUrl = getAvatarUrl(updatedUser)
        console.log('🖼️ Actualizando preview del avatar:', newAvatarUrl)
        if (newAvatarUrl) {
          // Agregar timestamp para forzar actualización de la imagen
          const avatarWithTimestamp = `${newAvatarUrl}?t=${Date.now()}`
          setAvatarPreview(avatarWithTimestamp)
        } else {
          // Si no hay URL, mantener el preview actual o limpiarlo
          console.warn('⚠️ No se pudo obtener URL del avatar para el preview')
        }
      } else {
        const errorMessage = response.data.error || 'Error al actualizar perfil'
        setError(errorMessage)
        
        if (response.data.details) {
          setFormErrors(response.data.details)
          
          // Mostrar errores específicos del avatar
          if (response.data.details.avatar) {
            const avatarErrors = Array.isArray(response.data.details.avatar) 
              ? response.data.details.avatar 
              : [response.data.details.avatar]
            
            const avatarErrorMessages = avatarErrors.map(err => {
              if (err.includes('validation.uploaded')) {
                return 'El archivo no se pudo subir correctamente. Por favor, intenta con otra imagen.'
              }
              if (err.includes('validation.image')) {
                return 'El archivo debe ser una imagen válida (JPG, PNG, GIF o WEBP).'
              }
              if (err.includes('validation.max')) {
                return 'El archivo es demasiado grande. El tamaño máximo es 5MB.'
              }
              return err
            })
            
            setError(avatarErrorMessages.join(' ') || errorMessage)
          }
        }
      }
    } catch (err) {
      console.error('❌ Error al actualizar perfil:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        statusText: err.response?.statusText,
        fullResponse: err.response
      })
      
      // Log detallado de la respuesta del error
      if (err.response?.data) {
        console.error('📋 Detalles del error del backend:', JSON.stringify(err.response.data, null, 2))
      }
      
      let errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Error al actualizar perfil'
      
      // Manejar errores específicos de validación
      if (err.response?.data?.details) {
        setFormErrors(err.response.data.details)
        
        // Mostrar errores específicos del avatar
        if (err.response.data.details.avatar) {
          const avatarErrors = Array.isArray(err.response.data.details.avatar) 
            ? err.response.data.details.avatar 
            : [err.response.data.details.avatar]
          
          const avatarErrorMessages = avatarErrors.map(errMsg => {
            if (typeof errMsg === 'string') {
              if (errMsg.includes('validation.uploaded')) {
                return 'El archivo no se pudo subir correctamente. Por favor, intenta con otra imagen o verifica que el tamaño no exceda los límites del servidor.'
              }
              if (errMsg.includes('validation.image')) {
                return 'El archivo debe ser una imagen válida (JPG, PNG, GIF o WEBP).'
              }
              if (errMsg.includes('validation.max')) {
                return 'El archivo es demasiado grande. El tamaño máximo permitido es 5MB.'
              }
              if (errMsg.includes('validation.mimes')) {
                return 'El formato del archivo no es válido. Por favor, usa JPG, PNG, GIF o WEBP.'
              }
            }
            return errMsg
          })
          
          errorMessage = avatarErrorMessages.join(' ') || errorMessage
        }
      }
      
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const getInitials = () => {
    const nombre = formData.nombre || user?.nombre || ''
    const apellido = formData.apellido || user?.apellido || ''
    const first = nombre[0]?.toUpperCase() || ''
    const second = apellido[0]?.toUpperCase() || ''
    return first + second || 'U'
  }

  if (initializing || !user) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader className="h-10 w-10 animate-spin mx-auto text-primary-600 dark:text-primary-400 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Cargando perfil...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 via-primary-600 to-gray-900 dark:from-white dark:via-primary-400 dark:to-white bg-clip-text text-transparent">
            Mi Perfil
          </h1>
          <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            Gestiona tu información personal y preferencias
          </p>
        </div>
      </div>

      {/* Mensajes de éxito/error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start space-x-3 animate-fade-in-down">
          <AlertCircle className="w-5 h-5 text-red-700 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-900 dark:text-red-200">Error</p>
            <p className="text-sm text-red-800 dark:text-red-300 mt-1">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-700 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-start space-x-3 animate-fade-in-down">
          <CheckCircle className="w-5 h-5 text-green-700 dark:text-green-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-900 dark:text-green-200">Éxito</p>
            <p className="text-sm text-green-800 dark:text-green-300 mt-1">{success}</p>
          </div>
          <button
            onClick={() => setSuccess(null)}
            className="text-green-700 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Columna izquierda - Avatar */}
          <div className="lg:col-span-1">
            <Card className="card-hover">
              <CardHeader>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Foto de Perfil</h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col items-center space-y-4">
                  {/* Avatar Preview */}
                  <div className="relative">
                    {previewUser ? (
                      <Avatar user={previewUser} size="w-32 h-32 text-3xl" showStatus={false} />
                    ) : null}
                    {avatarPreview && (
                      <button
                        type="button"
                        onClick={handleRemoveAvatar}
                        className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors shadow-md"
                        title="Eliminar avatar"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Botón para seleccionar imagen */}
                  <div className="w-full">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      className="hidden"
                      id="avatar-input"
                    />
                    <label
                      htmlFor="avatar-input"
                      className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors cursor-pointer"
                    >
                      <Camera className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {avatarPreview ? 'Cambiar Foto' : 'Subir Foto'}
                      </span>
                    </label>
                  </div>

                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                    Formatos: JPG, PNG, GIF (máx. 5MB)
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Columna derecha - Formulario */}
          <div className="lg:col-span-2">
            <Card className="card-hover">
              <CardHeader>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Información Personal</h2>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Nombre"
                      name="nombre"
                      required
                      leftIcon={User}
                      value={formData.nombre}
                      onChange={handleInputChange}
                      error={formErrors.nombre?.[0]}
                    />
                    <Input
                      label="Apellido"
                      name="apellido"
                      leftIcon={User}
                      value={formData.apellido}
                      onChange={handleInputChange}
                      error={formErrors.apellido?.[0]}
                    />
                  </div>

                  <Input
                    label="Email"
                    name="email"
                    type="email"
                    required
                    disabled
                    leftIcon={Mail}
                    value={formData.email}
                    error={formErrors.email?.[0]}
                    hint="El email no se puede modificar"
                  />

                  <Input
                    label="Teléfono"
                    name="telefono"
                    type="tel"
                    leftIcon={Phone}
                    value={formData.telefono}
                    onChange={handleInputChange}
                    error={formErrors.telefono?.[0]}
                  />

                  {isPetSitter ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        Estilo de cuidador
                      </label>
                      <select
                        name="estilo_perfil"
                        value={formData.estilo_perfil}
                        onChange={handleInputChange}
                        className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">No indicado</option>
                        <option value="pro">Pro (certificaciones, formal)</option>
                        <option value="onda">De onda (cercano, informal)</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Define cómo te presentas a las familias que buscan cuidador.
                      </p>
                    </div>
                  ) : null}

                  {/* Información adicional de solo lectura */}
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          Tipo de Usuario
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white capitalize">
                          {(user?.tipoUsuario || user?.tipo_usuario || '')?.replace('_', ' ').toLowerCase() || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          Estado de cuenta
                        </label>
                        <p className="mt-1 text-sm">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user.activo !== false
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                          }`}>
                            {user.activo !== false ? 'Cuenta activa' : 'Cuenta desactivada'}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-end space-x-3">
                <Button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center space-x-2"
                >
                  {loading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Guardando...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      <span>Guardar Cambios</span>
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </form>
    </div>
  )
}

